import styled from 'styled-components'

import { TextLink } from '@/ui/atoms/text-link'
import { MEDIA_QUERIES } from '@/ui/constants/mediaQueries'
import { styleFromTypography } from '@/ui/helpers/styleFromTypography'
import { type Theme } from '@/ui/theme/theme'

type ContentProps = {
  isShowingMore: boolean
  isSafari: boolean
}

export const ContentfulContentContainer = styled.div<ContentProps>(
  ({ isShowingMore, isSafari, theme }) => `
  align-items: ${isShowingMore ? 'center' : isSafari ? 'baseline' : 'flex-end'};
  display: flex;
  flex-direction: ${isShowingMore ? 'column' : 'row'};
  flex-wrap: wrap;
  margin-top: ${theme.spacing.default};
  max-width: ${isShowingMore ? '100rem' : '100%'};
`
)

const SafariContentStyle = ({ isShowingMore }: Pick<ContentProps, 'isShowingMore'>) => `
  & > p:first-of-type {
    overflow: hidden;
    white-space: ${isShowingMore ? 'normal' : 'nowrap'};
    text-overflow: ellipsis;
    display: -webkit-box;
  }

  & > p:not(:first-of-type), 
  & > h2 {
    display: ${isShowingMore ? 'block' : 'none'};
  }
`

const GeneralBrowsersContentStyle = ({
  theme,
  isShowingMore,
}: Pick<ContentProps, 'isShowingMore'> & {
  theme: Theme
}) => `
  -webkit-line-clamp: ${isShowingMore ? '' : '2'};
  -webkit-box-orient: vertical;
  color: ${theme.colors.grey.primary};
  display: -webkit-box;
  overflow: ${isShowingMore ? '' : 'hidden'};
  text-overflow: ${isShowingMore ? '' : 'ellipsis'};
`

export const ContentfulContentStyled = styled.div<ContentProps>(
  ({ isShowingMore, isSafari, theme }) => `
  max-width: 100%;
  width: 100%;
  
  ${
    isSafari
      ? SafariContentStyle({ isShowingMore })
      : `${GeneralBrowsersContentStyle({ theme, isShowingMore })}`
  }

  & p {
    margin: 0 0 ${isShowingMore ? theme.spacing.default : 0};
    ${styleFromTypography(theme.typography.body.medium)}
  }

  & h2 {
    margin: 0 0 ${theme.spacing.default};
    ${styleFromTypography(theme.typography.headline.medium)}
  }

  ${MEDIA_QUERIES(theme).medium}{
    max-width: 100rem;
  }
`
)

export const ContentfulContentShowMoreLink = styled(TextLink).attrs(() => ({ as: 'button' }))<{
  $isShowingMore: boolean
}>(
  ({ $isShowingMore, theme }) => `
  color: ${theme.colors.purple.primary};
  margin: ${
    $isShowingMore
      ? `${theme.spacing.defaultSmall} 0`
      : `0 0 ${theme.spacing.mini} ${theme.spacing.mini}`
  };
  text-decoration: underline;
`
)
